import { useEffect } from "react";

export function Modal({ open, onClose, title, children, footer, size = "md", testid }) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose?.();
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [open, onClose]);

    if (!open) return null;

    const sizeCls =
        size === "lg"
            ? "max-w-3xl"
            : size === "xl"
                ? "max-w-5xl"
                : size === "sm"
                    ? "max-w-md"
                    : "max-w-xl";

    return (
        <div
            data-testid={testid}
            className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        >
            <div
                data-testid={testid ? `${testid}-backdrop` : undefined}
                className="absolute inset-0 bg-white/60 backdrop-blur-xl"
                onClick={onClose}
            />
            <div
                className={`relative w-full ${sizeCls} glass-strong rounded-3xl overflow-hidden max-h-[90vh] flex flex-col`}
            >
                <div className="flex items-center justify-between px-6 sm:px-8 pt-6 shrink-0">
                    <h3 className="font-display text-xl sm:text-2xl tracking-tight text-black">
                        {title}
                    </h3>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        data-testid={testid ? `${testid}-close` : undefined}
                        className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-black/5"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
                <div className="px-6 sm:px-8 py-6 overflow-y-auto flex-1">{children}</div>
                {footer && (
                    <div className="px-6 sm:px-8 py-4 border-t border-black/5 bg-neutral-50/60 flex items-center justify-end gap-2 shrink-0">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

export function Field({ label, hint, children }) {
    return (
        <label className="block">
            <span className="block text-[11px] font-semibold tracking-[0.16em] uppercase text-neutral-500">
                {label}
            </span>
            <div className="mt-1.5">{children}</div>
            {hint && <div className="mt-1 text-[12px] text-neutral-400">{hint}</div>}
        </label>
    );
}

export function Input(props) {
    return (
        <input
            {...props}
            className={`w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-black placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-violet-300 ${props.className || ""}`}
        />
    );
}

export function Select({ children, ...rest }) {
    return (
        <select
            {...rest}
            className={`w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-black focus:outline-none focus:ring-2 focus:ring-violet-300 ${rest.className || ""}`}
        >
            {children}
        </select>
    );
}

export function TextArea(props) {
    return (
        <textarea
            {...props}
            className={`w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-black placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-violet-300 min-h-[92px] ${props.className || ""}`}
        />
    );
}

export function Button({
    variant = "primary",
    size = "md",
    children,
    className = "",
    ...rest
}) {
    const sizes = {
        sm: "text-[12px] px-3 py-1.5",
        md: "text-[13px] px-4 py-2",
        lg: "text-sm px-5 py-2.5",
    };
    const variants = {
        primary:
            "bg-black text-white hover:shadow-[0_12px_30px_-12px_rgba(139,92,246,0.6)]",
        ghost:
            "bg-white text-black border border-black/10 hover:bg-neutral-50",
        subtle:
            "bg-neutral-100 text-black hover:bg-neutral-200",
        danger:
            "bg-white text-rose-600 border border-rose-200 hover:bg-rose-50",
    };
    return (
        <button
            {...rest}
            className={`inline-flex items-center gap-1.5 rounded-full font-medium transition-all ${sizes[size]} ${variants[variant]} ${className}`}
        >
            {children}
        </button>
    );
}

export function StatusPill({ status }) {
    const map = {
        active: "bg-emerald-50 text-emerald-700",
        success: "bg-emerald-50 text-emerald-700",
        accepted: "bg-emerald-50 text-emerald-700",
        trial: "bg-violet-50 text-violet-700",
        invited: "bg-violet-50 text-violet-700",
        sent: "bg-violet-50 text-violet-700",
        pending: "bg-violet-50 text-violet-700",
        suspended: "bg-amber-50 text-amber-700",
        expired: "bg-amber-50 text-amber-700",
        inactive: "bg-neutral-100 text-neutral-600",
        cancelled: "bg-neutral-100 text-neutral-600",
        removed: "bg-rose-50 text-rose-700",
        failed: "bg-rose-50 text-rose-700",
    };
    const cls = map[String(status || "").toLowerCase()] || "bg-neutral-100 text-neutral-600";
    return (
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${cls}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
            {String(status || "—").toUpperCase()}
        </span>
    );
}

export function EmptyState({ title = "Nothing here yet", hint }) {
    return (
        <div className="rounded-2xl border border-dashed border-black/10 bg-white/60 p-10 text-center">
            <div className="font-display text-lg text-neutral-700">{title}</div>
            {hint && <div className="mt-1 text-[13px] text-neutral-500">{hint}</div>}
        </div>
    );
}
